/* eslint-disable ngrx/avoid-mapping-selectors */
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { format } from 'date-fns';
import { combineLatest, map, Observable } from 'rxjs';

import { AutoPaySelectors } from '@amfam/billing/auto-pay/data-access';
import { BillAccountsSelectors } from '@amfam/billing/billaccount/data-access';
import { ScheduledPaymentSelectors } from '@amfam/billing/schedulepayment/data-access';
import { BillingStatusEnums } from '@amfam/billing/shared/ui';
import { PolicySelectors } from '@amfam/policy/data-access';
import { PropertyPolicy } from '@amfam/policy/models';
import {
  AmountBreakdown,
  AutomaticPayment,
  BillAccount,
  BillingDashBoardBillAccount,
  PaymentStatus,
  ScheduledPayment,
  StatusObj
} from '@amfam/shared/models';
import { CurrencyUSDPipe } from '@amfam/shared/ui/pipes';
import { CopyService } from '@amfam/shared/utility/shared-services';

@Injectable({ providedIn: 'root' })
export class CommonFeatureFacade {
  constructor(
    private store: Store,
    private copyService: CopyService
  ) {}

  dataLoading(): Observable<boolean> {
    return combineLatest([
      this.store.select(BillAccountsSelectors.selectBillAccountsLoading),
      this.store.select(BillAccountsSelectors.selectBillAccountsAnyLoading),
      this.store.select(AutoPaySelectors.getAnyLoading)
    ]).pipe(
      map(
        ([billAccountsLoading, billAccountsAnyLoading, autopayLoading]) =>
          billAccountsLoading || billAccountsAnyLoading || autopayLoading
      )
    );
  }

  calculateManualPaymentTotal(): Observable<AmountBreakdown> {
    return combineLatest([
      this.store.select(BillAccountsSelectors.selectBillAccounts),
      this.store.select(AutoPaySelectors.getAutomaticPayments)
    ]).pipe(
      map(([billAccounts, automaticPayments]) => {
        let totalMinDue = 0;
        // filter billAccounts with AFT accounts
        billAccounts = billAccounts.filter(acc => !acc.enabledAFT);
        if (automaticPayments.length) {
          automaticPayments.map(ap => {
            billAccounts = billAccounts.filter(
              acc => acc.billAccountNumber !== ap?.billAccountNumber
            );
          });
          billAccounts.map(
            billAccount =>
              (totalMinDue += Math.round(Number(billAccount.minimumAmountDue) * 100) / 100)
          );
        } else {
          billAccounts.map(
            billAccount =>
              (totalMinDue += Math.round(Number(billAccount.minimumAmountDue) * 100) / 100)
          );
        }
        const title = this.copyService.getCopy('billing.billingDashboard.manualPayment');
        return { totalMinDue, title };
      }),
      map(totalObject => {
        const amount = new CurrencyUSDPipe().transform(totalObject.totalMinDue);
        return {
          title: totalObject.title,
          dollars: amount.substring(0, amount.indexOf('.')),
          cents: amount.substring(amount.length - 3),
          total: totalObject.totalMinDue
        };
      })
    );
  }

  calculateAutopayOrAFTTotal(): Observable<AmountBreakdown> {
    return combineLatest([this.calculateAutopayAmount(), this.calculateAFTAmount()]).pipe(
      map(([totalAutopayAmount, totalAFTAmount]) => {
        const total = totalAutopayAmount + totalAFTAmount;
        const title =
          totalAFTAmount === 0
            ? this.copyService.getCopy('billing.billingDashboard.autoPayment')
            : totalAutopayAmount === 0
              ? this.copyService.getCopy('billing.billingDashboard.AFTPayment')
              : this.copyService.getCopy('billing.billingDashboard.autoPayAndAFTBoth');
        return { total, title };
      }),
      map(totalObject => {
        const amount = new CurrencyUSDPipe().transform(totalObject.total);
        return {
          title: totalObject.title,
          dollars: amount.substring(0, amount.indexOf('.')),
          cents: amount.substring(amount.length - 3),
          total: totalObject.total
        };
      })
    );
  }

  calculateAutopayAmount(): Observable<number> {
    return this.store.select(AutoPaySelectors.getAutomaticPayments).pipe(
      map(automaticPayments => {
        let totalAutopayAmount = 0;
        if (automaticPayments.length) {
          // filter automaticPayments when we do not have exact predictedDollarAmount for next payment or if the automaticPayment object is undefined/null
          automaticPayments
            .filter(
              automaticPayment =>
                automaticPayment?.predictedDollarAmount !== 'UNKNOWN' &&
                automaticPayment?.predictedDollarAmount !== '0' &&
                automaticPayment?.billAccountNumber
            )
            .map(
              automaticPayment =>
                (totalAutopayAmount +=
                  Math.round(Number(automaticPayment?.predictedDollarAmount) * 100) / 100)
            );
        }
        return totalAutopayAmount;
      })
    );
  }

  calculateAFTAmount(): Observable<number> {
    return this.store.select(BillAccountsSelectors.selectBillAccounts).pipe(
      map(billAccounts => {
        let totalAFTAmount = 0;
        const billAccountsEnrolledInAFT = billAccounts.filter(
          billAccount => billAccount.enabledAFT
        );
        if (billAccountsEnrolledInAFT.length) {
          billAccountsEnrolledInAFT.map(
            billAccount =>
              (totalAFTAmount += Math.round(Number(billAccount.minimumAmountDue) * 100) / 100)
          );
        }
        return totalAFTAmount;
      })
    );
  }

  getBillingTileAccountsList(): Observable<BillingDashBoardBillAccount[]> {
    return combineLatest([
      this.store.select(BillAccountsSelectors.selectBillAccounts),
      this.store.select(AutoPaySelectors.getAutomaticPayments),
      this.store.select(ScheduledPaymentSelectors.selectScheduledPaymentsWithMostPaymentStatus),
      this.store.select(PolicySelectors.selectPropertyPoliciesWithTPI)
    ]).pipe(
      map(([billAccounts, automaticPayments, scheduledPayments, policiesWithTPI]) => {
        const billingTileAccountsList: BillingDashBoardBillAccount[] = [];
        // do not show bill accounts on tile when minimumAmountDue is 0
        billAccounts = billAccounts.filter(acc => acc.minimumAmountDue > '0');
        billAccounts.map(account => {
          const isTPI = policiesWithTPI.find(
            policy => policy.billingAccountNumber === account.billAccountNumber
          );
          billingTileAccountsList.push({
            billAccountNumber: account.billAccountNumber,
            amount: account.minimumAmountDue,
            icon: account.icon,
            status: this.getAccountStatus(account, scheduledPayments, automaticPayments, isTPI)
          });
        });

        /**
         * DP: As per the final discussion of sorting, we are going to sort by
         * 1. Past Due
         * 2. Due Today.
         * 3. Due Soon and Date (When minimumdue is >0.
         * 4. Paid Thru Date. (When account balance is 0.) this does not apply to billing tile sorting
         * 5. Nothing Due. (When minimum due is not available). this does not apply to billing tile sorting
         * not have any additional sorting on tables. (AFT,Scheduled Payment, Automatic Payment, managedElsewhere)
         */
        const pastDueArr: BillingDashBoardBillAccount[] = [];
        const dueTodayArr: BillingDashBoardBillAccount[] = [];
        const dueSoonArr: BillingDashBoardBillAccount[] = [];
        const defaultArr: BillingDashBoardBillAccount[] = [];

        billingTileAccountsList.forEach(ba => {
          if (ba.status) {
            switch (ba.status.statusText) {
              case BillingStatusEnums.PAST_DUE:
                pastDueArr.push(ba);
                break;
              // case BillingStatusEnums.DUE_TODAY:
              //   dueTodayArr.push(ba);
              //   break;
              case BillingStatusEnums.DUE_SOON:
                dueSoonArr.push(ba);
                break;
              default:
                defaultArr.push(ba);
                break;
            }
          } else {
            defaultArr.push(ba);
          }
        });
        const SortedList = [...pastDueArr, ...dueTodayArr, ...dueSoonArr, ...defaultArr];
        return SortedList;
      })
    );
  }

  /*
  DP: This function gets the current status of the bill account and will be used for bill account status pill according to pill hierarchy tree in figma
  */
  getAccountStatus(
    billAccount: BillAccount,
    scheduledPayments: ScheduledPayment[],
    automaticPayments: AutomaticPayment[],
    isTPI: PropertyPolicy
  ): StatusObj {
    const statusObj = {
      statusText: '',
      statusSubText: 'Due ' + format(billAccount.paymentDueDate, 'MM/DD/YYYY')
    };
    // as of now managedElsewhere is not included in pill status hierarchy tree, so passing null status
    const managedElsewhere = billAccount.registeredElsewhere || billAccount.isExternalBam;
    if (managedElsewhere) {
      return null;
    }
    /*
    Show account status in the following order as per pill status hierarchy tree
    1. AFT
    2. Scheduled Payment (unsuccessful, processing, scheduled)
    3. past due
    4. Automatic Payment
    5. TPI
    6. Payment due (due soon, nothing due)
  */

    // 1. If the account is AFT enabled, show AFT
    if (billAccount.enabledAFT) {
      statusObj.statusText = BillingStatusEnums.AFT;
      return statusObj;
    }

    /*
      2. Determine if there are scheduled payments.In case of multiple schedule
      payment need to display the schedule payment date which is earliest.
    */
    const billAccountPayments = [];
    scheduledPayments.forEach(payment => {
      if (payment) {
        payment.billAccounts.forEach(acc => {
          billAccountPayments.push({
            billAccountNumber: acc.billAccountNumber,
            billAcctPaymentAmount: acc.billAcctPaymentAmount,
            receivedDate: payment.receivedDate,
            paymentStatus: payment.paymentStatus
          });
        });
      }
    });

    const matchingPayment = billAccountPayments
      .sort((a, b) => String(a['receivedDate']).localeCompare(String(b['receivedDate'])))
      .reverse()
      .find(
        billAccountPayment => billAccount.billAccountNumber === billAccountPayment.billAccountNumber
      );

    // continue only if there is matching payment
    if (matchingPayment && matchingPayment.paymentStatus !== PaymentStatus.SUCCESSFUL) {
      // check to see if there is still min due amount after payment is scheduled
      const isPaymentSameAsMinDue =
        Number(matchingPayment.billAcctPaymentAmount) >= Number(billAccount.minimumAmountDue);

      switch (matchingPayment.paymentStatus) {
        case PaymentStatus.UNSUCCESSFUL:
          statusObj.statusText = BillingStatusEnums.FAILED;
          break;
        case PaymentStatus.AUTHORIZED:
          statusObj.statusText =
            isPaymentSameAsMinDue || Number(billAccount.minimumAmountDue) === 0
              ? BillingStatusEnums.PROCESSING
              : BillingStatusEnums.DUE_SOON;
          break;
        case PaymentStatus.SCHEDULED:
          statusObj.statusText =
            isPaymentSameAsMinDue || Number(billAccount.minimumAmountDue) === 0
              ? BillingStatusEnums.SCHEDULED
              : BillingStatusEnums.DUE_SOON;
          break;
      }
      return statusObj;
    }

    // 3. check to see if bill account is past due before checking for autopay
    if (billAccount.pastDue) {
      statusObj.statusText = BillingStatusEnums.PAST_DUE;
      return statusObj;
    }

    // 4. Determine if we have autopay information to show
    const matchingAutoPay = automaticPayments.find(
      autopay => autopay?.billAccountNumber === billAccount.billAccountNumber
    );

    if (matchingAutoPay) {
      statusObj.statusText = BillingStatusEnums.AUTO_PAY;
      return statusObj;
    }

    // 5. TPI - with account balance 0
    // TPI with other risk with some account balance/min due will be handled through above conditions.
    if (isTPI && Number(billAccount.accountBalance) === 0) {
      statusObj.statusText = BillingStatusEnums.ESCROW;
      return statusObj;
    }
    // TPI with other risk with min due 0, need to set the status as paid as per hierarchy tree.
    if (isTPI && Number(billAccount.minimumAmountDue) === 0) {
      statusObj.statusText = BillingStatusEnums.PAID;
      return statusObj;
    }

    // 5. Default bill account status
    if (Number(billAccount.minimumAmountDue) > 0) {
      statusObj.statusText = BillingStatusEnums.DUE_SOON;
      return statusObj;
    } else {
      return null;
    }
  }
}
